import React from "react";
import styled from "styled-components";
import { useTranslate } from "@tolgee/react";
import { ChevronDown, Calendar } from "lucide-react";
import MeetingDateTimePicker from "./MeetingDateTimePicker";
import AfterOccurrencesStepper from "./AfterOccurrencesStepper";
import {
  maxEndDate,
  maxOccurrences,
  effectiveEndsInfo,
  formatRecurrenceSummary,
  WEEKDAY_KEYS,
  ORDINAL_KEYS,
} from "../../Helpers/recurrenceHelper";

const ORDINAL_OPTIONS = [1, 2, 3, 4, -1];

const RecurrenceFieldsCard = ({ startDate, pattern, onChange, fieldsLocked, lockedMessage }) => {
  const { t } = useTranslate();
  const isRecurring = pattern.frequency !== "none";
  const cap = maxEndDate(startDate);
  const max = isRecurring ? maxOccurrences(pattern, startDate) : 1;

  const patch = (changes) => onChange({ ...pattern, ...changes });

  const toggleWeekday = (day) => {
    const days = pattern.weeklyDaysOfWeek.includes(day)
      ? pattern.weeklyDaysOfWeek.filter((d) => d !== day)
      : [...pattern.weeklyDaysOfWeek, day];
    if (days.length === 0) return;
    patch({ weeklyDaysOfWeek: days });
  };

  const summary = isRecurring
    ? formatRecurrenceSummary(t, pattern, effectiveEndsInfo(pattern, startDate))
    : null;

  return (
    <OptionWrapper>
      <Row>
        <Label>{t("meetings.repeat")}</Label>
        <SelectWrapper>
          <Select
            value={pattern.frequency}
            disabled={fieldsLocked}
            onChange={(e) => patch({ frequency: e.target.value })}
          >
            <option value="none">{t("meetings.does_not_repeat")}</option>
            <option value="daily">{t("meetings.frequency_daily")}</option>
            <option value="weekly">{t("meetings.frequency_weekly")}</option>
            <option value="monthly">{t("meetings.frequency_monthly")}</option>
          </Select>
          <SelectChevron size={14} />
        </SelectWrapper>
      </Row>

      {isRecurring && fieldsLocked && (
        <LockedNote>{lockedMessage || t("meetings.repeat_pattern_locked_note")}</LockedNote>
      )}

      {pattern.frequency === "weekly" && !fieldsLocked && (
        <>
          <Separator />
          <WeekdaySection>
            <SectionLabel>{t("meetings.occurs_on")}</SectionLabel>
            <WeekdayRow>
              {WEEKDAY_KEYS.map((key, day) => (
                <WeekdayChip
                  key={key}
                  type="button"
                  $active={pattern.weeklyDaysOfWeek.includes(day)}
                  onClick={() => toggleWeekday(day)}
                >
                  {t(key)}
                </WeekdayChip>
              ))}
            </WeekdayRow>
          </WeekdaySection>
        </>
      )}

      {pattern.frequency === "monthly" && !fieldsLocked && (
        <>
          <Separator />
          <MonthlyPatternSection>
            <MonthlyOptionRow>
              <RadioOuter
                $active={pattern.monthlyPatternType === "day_of_month"}
                onClick={() => patch({ monthlyPatternType: "day_of_month" })}
              >
                <RadioDot $active={pattern.monthlyPatternType === "day_of_month"} />
              </RadioOuter>
              <MonthlyLabel>{t("meetings.day")}</MonthlyLabel>
              <SelectWrapper $compact>
                <Select
                  value={pattern.monthlyDayOfMonth}
                  disabled={pattern.monthlyPatternType !== "day_of_month"}
                  onChange={(e) => patch({ monthlyDayOfMonth: Number(e.target.value), monthlyPatternType: "day_of_month" })}
                >
                  {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                    <option key={day} value={day}>{day}</option>
                  ))}
                </Select>
                <SelectChevron size={12} />
              </SelectWrapper>
              <MonthlyLabel>{t("meetings.month_day_of")}</MonthlyLabel>
            </MonthlyOptionRow>

            <MonthlyOptionRow>
              <RadioOuter
                $active={pattern.monthlyPatternType === "nth_weekday"}
                onClick={() => patch({ monthlyPatternType: "nth_weekday" })}
              >
                <RadioDot $active={pattern.monthlyPatternType === "nth_weekday"} />
              </RadioOuter>
              <SelectWrapper $compact>
                <Select
                  value={pattern.monthlyWeekOrdinal}
                  disabled={pattern.monthlyPatternType !== "nth_weekday"}
                  onChange={(e) => patch({ monthlyWeekOrdinal: Number(e.target.value), monthlyPatternType: "nth_weekday" })}
                >
                  {ORDINAL_OPTIONS.map((ordinal) => (
                    <option key={ordinal} value={ordinal}>
                      {t(ORDINAL_KEYS[ordinal])}
                    </option>
                  ))}
                </Select>
                <SelectChevron size={12} />
              </SelectWrapper>
              <SelectWrapper $compact>
                <Select
                  value={pattern.monthlyWeekday}
                  disabled={pattern.monthlyPatternType !== "nth_weekday"}
                  onChange={(e) => patch({ monthlyWeekday: Number(e.target.value), monthlyPatternType: "nth_weekday" })}
                >
                  {WEEKDAY_KEYS.map((key, day) => (
                    <option key={key} value={day}>{t(key)}</option>
                  ))}
                </Select>
                <SelectChevron size={12} />
              </SelectWrapper>
              <MonthlyLabel>{t("meetings.month_day_of")}</MonthlyLabel>
            </MonthlyOptionRow>
          </MonthlyPatternSection>
        </>
      )}

      {isRecurring && (
        <>
          <Separator />
          <EndsSection>
            <SectionLabel>{t("meetings.ends")}</SectionLabel>

            <EndsRow>
              <RadioOuter
                $active={pattern.endsType === "on_date"}
                disabled={fieldsLocked}
                onClick={() => !fieldsLocked && patch({ endsType: "on_date" })}
              >
                <RadioDot $active={pattern.endsType === "on_date"} />
              </RadioOuter>
              <EndsLabel $grow $muted={pattern.endsType !== "on_date"}>{t("meetings.on_date")}</EndsLabel>
              <MeetingDateTimePicker
                slim
                format="dd-MM-yyyy"
                value={pattern.endsOnDate}
                setValue={(date) => patch({ endsOnDate: date })}
                maxDate={cap}
                noCalendar
                disabled={fieldsLocked || pattern.endsType !== "on_date"}
              />
            </EndsRow>

            <EndsRow>
              <RadioOuter
                $active={pattern.endsType === "after_occurrences"}
                disabled={fieldsLocked}
                onClick={() => !fieldsLocked && patch({ endsType: "after_occurrences" })}
              >
                <RadioDot $active={pattern.endsType === "after_occurrences"} />
              </RadioOuter>
              <EndsLabel $grow $muted={pattern.endsType !== "after_occurrences"}>{t("meetings.after")}</EndsLabel>
              <AfterOccurrencesStepper
                value={pattern.endsAfterOccurrences || 1}
                max={max}
                disabled={fieldsLocked || pattern.endsType !== "after_occurrences"}
                onChange={(value) => patch({ endsAfterOccurrences: value })}
              />
              <EndsLabel $muted>{t("meetings.occurrences")}</EndsLabel>
            </EndsRow>

            {summary && (
              <SummaryText>
                <Calendar size={13} />
                {summary}
              </SummaryText>
            )}
          </EndsSection>
        </>
      )}
    </OptionWrapper>
  );
};

export default RecurrenceFieldsCard;

const OptionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.menu.groupContentBackground};
  border-radius: 4px;
  width: 100%;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
`;

const Label = styled.div`
  flex: 1;
  color: ${({ theme }) => theme.menu.primaryTextColor};
  font-size: 16px;
  font-weight: 500;
`;

const Separator = styled.div`
  width: 100%;
  min-height: 1px;
  background: ${({ theme }) => theme.sidebar.borderColor};
`;

const SelectWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  min-width: ${({ $compact }) => ($compact ? "0" : "auto")};
`;

const Select = styled.select`
  appearance: none;
  background: ${({ theme }) => theme.menu.enterPhoneNumberBackground};
  border: none;
  border-radius: 10px;
  padding: 8px 26px 8px 12px;
  color: ${({ theme }) => theme.menu.primaryTextColor};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: none;
  }
`;

const SelectChevron = styled(ChevronDown)`
  position: absolute;
  right: 8px;
  pointer-events: none;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
`;

const LockedNote = styled.div`
  padding: 0 16px 14px 16px;
  font-size: 12px;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  line-height: 16px;
`;

const WeekdaySection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px 16px 12px 16px;
`;

const SectionLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
`;

const WeekdayRow = styled.div`
  display: flex;
  gap: 6px;
`;

const WeekdayChip = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1.5px solid ${({ theme, $active }) => ($active ? theme.menu.selectedTextColor : "transparent")};
  background: ${({ theme, $active }) => ($active ? theme.chat.selectedBackground : theme.menu.enterPhoneNumberBackground)};
  color: ${({ theme, $active }) => ($active ? theme.menu.selectedTextColor : theme.menu.secondaryTextColor)};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MonthlyPatternSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 14px 16px 12px 16px;
`;

const MonthlyOptionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const MonthlyLabel = styled.div`
  color: ${({ theme }) => theme.menu.primaryTextColor};
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
`;

const EndsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px 16px 16px 16px;
`;

const EndsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const EndsLabel = styled.div`
  flex: ${({ $grow }) => ($grow ? "1" : "0 0 auto")};
  color: ${({ theme, $muted }) => ($muted ? theme.menu.secondaryTextColor : theme.menu.primaryTextColor)};
  font-size: 14px;
  font-weight: 500;
`;

const RadioOuter = styled.button.attrs({ type: "button" })`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 1.6px solid ${({ theme, $active }) => ($active ? theme.menu.selectedTextColor : "#5A5D68")};
  background: none;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  padding: 0;

  &:disabled {
    opacity: 0.5;
  }
`;

const RadioDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ theme, $active }) => ($active ? theme.menu.selectedTextColor : "transparent")};
`;

const SummaryText = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid ${({ theme }) => theme.sidebar.borderColor};
  font-size: 13px;
  color: ${({ theme }) => theme.menu.secondaryTextColor};

  svg {
    flex-shrink: 0;
  }
`;
